import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { useAuth } from "../lib/AuthContext";
import { useCart } from "../lib/CartContext";
import { LiveBadge } from "../components/Layout";
import ImmersiveControls, { useLocalLike } from "../components/ImmersiveControls";
import { Heart, Users, Send, Store, Pin, X, ChevronUp, ChevronDown, Play, Pause, Square } from "lucide-react";
import SaveButton from "../components/SaveButton";

export default function LivePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  // Cache of fetched stream details across navigations so swipes don't
  // refetch the page they just came from.
  const dataCacheRef = useRef(new Map());
  const [data, setData] = useState(() => dataCacheRef.current.get(id) || null);
  const [allStreams, setAllStreams] = useState([]);
  const [msg, setMsg] = useState("");
  const [chat, setChat] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [selectedP, setSelectedP] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [swipeHint, setSwipeHint] = useState(false);
  // Direction of the most recent swipe, drives the slide animation:
  //   1  → next stream (incoming from below)
  //  -1  → prev stream (incoming from above)
  //   0  → no animation (initial mount)
  const [slideDir, setSlideDir] = useState(0);
  const [liked, toggleLike] = useLocalLike(`stream_${id}`);
  // Host-only controls — pause / resume / end the live broadcast
  const [hostBusy, setHostBusy] = useState(false);
  const isHost = !!(user && data?.store && data.store.owner_id === user.user_id);
  const isAdmin = user?.role === "admin";
  const canControl = isHost || isAdmin;
  const isPaused = !!data?.stream?.is_paused;
  const isEnded = data?.stream && data.stream.is_live === false;
  const chatRef = useRef(null);
  const touchStartY = useRef(null);
  const wheelCooldown = useRef(false);

  const togglePause = async () => {
    if (!canControl || hostBusy) return;
    setHostBusy(true);
    try {
      const { data: r } = await api.post(`/seller/stream/${id}/pause`);
      // Optimistically mirror the new state into local view
      setData((d) => d ? ({ ...d, stream: { ...d.stream, is_paused: r.is_paused } }) : d);
      dataCacheRef.current.set(id, { ...data, stream: { ...data.stream, is_paused: r.is_paused } });
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to toggle pause");
    } finally { setHostBusy(false); }
  };

  const endStream = async () => {
    if (!canControl || hostBusy) return;
    if (!window.confirm("End the live stream? Viewers will be sent to other shows. This can't be undone.")) return;
    setHostBusy(true);
    try {
      await api.post(`/seller/stream/${id}/end`);
      setData((d) => d ? ({ ...d, stream: { ...d.stream, is_live: false, is_paused: false } }) : d);
      // Send the host back to the live index where they can start a new one
      setTimeout(() => nav("/seller"), 800);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to end stream");
    } finally { setHostBusy(false); }
  };

  // Load (or replay from cache) the current stream
  useEffect(() => {
    const cached = dataCacheRef.current.get(id);
    if (cached) {
      setData(cached);
      setChat(cached.chat || []);
      // Re-fetch in the background to get fresh viewer counts / new chat
      api.get(`/streams/${id}`).then(({ data: fresh }) => {
        dataCacheRef.current.set(id, fresh);
        setData(fresh);
      }).catch(() => {});
      return;
    }
    let cancelled = false;
    api.get(`/streams/${id}`).then(({ data: fresh }) => {
      if (cancelled) return;
      dataCacheRef.current.set(id, fresh);
      setData(fresh);
      setChat(fresh.chat || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Load list of live streams once for navigation
  useEffect(() => {
    api.get("/streams?live_only=true").then(({data}) => setAllStreams(data));
  }, []);

  // First-visit hint: shows once per browser, auto-hides after 3s.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("o2s.live_hint_seen") === "1") return;
    setSwipeHint(true);
    const t = setTimeout(() => {
      setSwipeHint(false);
      try { localStorage.setItem("o2s.live_hint_seen", "1"); } catch {}
    }, 3000);
    return () => clearTimeout(t);
  }, [id]);

  useEffect(() => {
    chatRef.current?.scrollTo(0, chatRef.current.scrollHeight);
  }, [chat]);

  // Simulated chat
  useEffect(() => {
    const samples = ["fire drop 🔥", "OMG need this", "how is the sizing?", "LFG", "shipping to UK?", "just copped 👟", "more coming?"];
    const t = setInterval(() => {
      const fake = {
        chat_id: `sim_${Date.now()}`,
        user_name: ["leo", "mia", "zoe", "kai", "ryo", "sam"][Math.floor(Math.random()*6)],
        picture: "",
        text: samples[Math.floor(Math.random()*samples.length)],
        created_at: new Date().toISOString(),
      };
      setChat(c => [...c.slice(-40), fake]);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  // Find current index + goto helpers
  const idx = allStreams.findIndex(s => s.stream_id === id);
  const goTo = useCallback((delta) => {
    if (!allStreams.length) return;
    const n = allStreams.length;
    const nextIdx = ((idx === -1 ? 0 : idx) + delta + n) % n;
    const next = allStreams[nextIdx];
    if (next && next.stream_id !== id) {
      setSlideDir(delta > 0 ? 1 : -1);
      // `replace: true` keeps the back-button stack tidy when users
      // flick through dozens of streams in a row.
      nav(`/live/${next.stream_id}`, { replace: true });
    }
  }, [allStreams, idx, id, nav]);

  // Prefetch the immediate neighbours so the next swipe is instant.
  // Both data (chat, pinned products) and the actual <video> source — the
  // browser will decode + buffer the next clip's first frame ahead of time.
  const neighbours = useMemo(() => {
    if (!allStreams.length || idx === -1) return [];
    const n = allStreams.length;
    return [allStreams[(idx + 1) % n], allStreams[(idx - 1 + n) % n]]
            .filter(s => s && s.stream_id !== id);
  }, [allStreams, idx, id]);

  useEffect(() => {
    neighbours.forEach((s) => {
      if (!s || dataCacheRef.current.has(s.stream_id)) return;
      api.get(`/streams/${s.stream_id}`).then(({ data: d }) => {
        dataCacheRef.current.set(s.stream_id, d);
      }).catch(() => {});
    });
  }, [neighbours]);

  // Snap the slide animation back to neutral after the keyframes finish so
  // the next frame doesn't keep the slide-up class hanging around.
  useEffect(() => {
    if (!slideDir) return;
    const t = setTimeout(() => setSlideDir(0), 320);
    return () => clearTimeout(t);
  }, [slideDir, id]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (["ArrowDown", "ArrowRight", "PageDown", "j"].includes(e.key)) { e.preventDefault(); goTo(1); }
      if (["ArrowUp", "ArrowLeft", "PageUp", "k"].includes(e.key)) { e.preventDefault(); goTo(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goTo]);

  // Mouse wheel — tighter cooldown so power users can rip through streams.
  const onWheel = (e) => {
    if (wheelCooldown.current) return;
    if (Math.abs(e.deltaY) < 24) return;
    wheelCooldown.current = true;
    setTimeout(() => { wheelCooldown.current = false; }, 350);
    goTo(e.deltaY > 0 ? 1 : -1);
  };

  // Touch swipe — only treat as a stream switch when it's a fast vertical flick.
  // Otherwise let the page scroll normally so users can reach chat / pinned drops.
  const touchStartX = useRef(null);
  const touchStartT = useRef(0);
  const onTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    touchStartT.current = Date.now();
  };
  const onTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const dx = e.changedTouches[0].clientX - (touchStartX.current ?? 0);
    const elapsed = Date.now() - (touchStartT.current || 0);
    // Slightly looser thresholds — feels snappier but still ignores
    // accidental scrolls / horizontal pans.
    const fastFlick = elapsed < 500 && Math.abs(dy) > 80 && Math.abs(dx) < 80;
    if (fastFlick) goTo(dy < 0 ? 1 : -1);
    touchStartY.current = null; touchStartX.current = null;
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    if (!user) return alert("Login to chat");
    try {
      const { data } = await api.post(`/streams/${id}/chat`, { text: msg });
      setChat(c => [...c, data]);
      setMsg("");
    } catch { alert("Failed. Login again."); }
  };

  const sendReact = async () => {
    const emojis = ["❤️", "🔥", "💸", "✨", "💯"];
    const e = emojis[Math.floor(Math.random()*emojis.length)];
    const id_ = Date.now();
    setReactions(r => [...r, { id: id_, e, x: Math.random()*60 + 20 }]);
    setTimeout(() => setReactions(r => r.filter(x => x.id !== id_)), 2000);
    try { await api.post(`/streams/${id}/react`); } catch {}
  };

  const handleAddToCart = async () => {
    if (!selectedP) return;
    if (selectedP.sizes?.length && !selectedSize) return alert("Pick a size");
    await addItem(selectedP.product_id, selectedSize, 1);
    setSelectedP(null);
    setSelectedSize(null);
  };

  // Fall back to the entry from the streams list while the detail
  // request is in-flight, so the video swaps instantly on swipe.
  const listEntry = allStreams.find(s => s.stream_id === id);
  const view = data || (listEntry ? { stream: listEntry, store: { name: "", store_id: "" }, pinned_products: [] } : null);
  if (!view) return <div className="p-10 text-center text-[#A1A1AA]">Loading stream…</div>;
  const slideClass = slideDir === 1  ? "live-slide-in-up"
                  : slideDir === -1 ? "live-slide-in-down"
                  : "";

  return (
    <div className="pb-24 md:pb-0">
      <div className="grid lg:grid-cols-[1fr_380px] gap-0 lg:max-w-7xl lg:mx-auto">
        {/* VIDEO */}
        <div
          className="relative bg-black"
          onWheel={onWheel}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className={`aspect-[9/16] lg:aspect-video max-h-[85vh] relative overflow-hidden ${slideClass}`}>
            <video key={id} src={view.stream.video_url} autoPlay loop muted playsInline preload="auto" className="w-full h-full object-cover"/>
            {/* Pause / end overlays — both viewers and host see these */}
            {isPaused && view.stream.is_live && (
              <div className="absolute inset-0 bg-black/85 flex items-center justify-center z-30 backdrop-blur-sm"
                data-testid="live-paused-overlay">
                <div className="text-center px-6">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF007F] mb-2">Paused</div>
                  <div className="font-display font-bold text-4xl uppercase">Back in a sec</div>
                  <div className="text-sm text-[#A1A1AA] mt-2">{view.stream.host_name || view.store?.name} stepped away — stream resumes shortly.</div>
                </div>
              </div>
            )}
            {isEnded && (
              <div className="absolute inset-0 bg-black/90 flex items-center justify-center z-30 backdrop-blur-sm"
                data-testid="live-ended-overlay">
                <div className="text-center px-6">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] mb-2">Show ended</div>
                  <div className="font-display font-bold text-4xl uppercase">Thanks for watching<span className="text-[#D4FF00]">.</span></div>
                  <div className="text-sm text-[#A1A1AA] mt-3 mb-4">Swipe up or pick another live show below.</div>
                  <Link to="/live" className="btn-primary inline-block">Browse live shows</Link>
                </div>
              </div>
            )}
            {/* Hidden pre-loader videos for the next/prev streams so their
                first frames are already buffered when the user swipes. */}
            {neighbours.map((s) => (
              <video key={`pre-${s.stream_id}`} src={s.video_url} muted preload="auto"
                className="absolute w-px h-px opacity-0 pointer-events-none" aria-hidden="true"/>
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none"/>

            {/* top bar */}
            <div className="absolute top-3 left-3 right-3 flex items-center gap-2 z-10">
              <button
                onClick={() => (window.history.length > 1 ? nav(-1) : nav("/live"))}
                data-testid="live-close-btn"
                className="w-9 h-9 rounded-full bg-black/60 text-white hover:bg-[#D4FF00] hover:text-black flex items-center justify-center active:scale-95 shrink-0"
                aria-label="Back"
              >
                <X size={16} strokeWidth={2.5}/>
              </button>
              <ImmersiveControls
                liked={liked}
                onLike={toggleLike}
                shareTitle={view.stream.title}
              />
              {view.stream.is_live && <div className="ml-auto flex items-center gap-2"><LiveBadge/><div className="bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                <Users size={12}/> {view.stream.viewers}
              </div></div>}
            </div>
            <div className="absolute top-14 right-3 z-10 flex items-center gap-2">
              <SaveButton kind="stream" id={view.stream.stream_id} variant="overlay"/>
              {view.store?.store_id && (
                <Link to={`/store/${view.store?.store_id}`} className="bg-white/10 backdrop-blur text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 hover:bg-white/20">
                  <Store size={12}/> {view.store?.name}
                </Link>
              )}
            </div>

            {/* Host-only controls — Pause / Resume + End. Appears centred at
                top so the host doesn't have to fish through nav arrows. */}
            {canControl && view.stream.is_live && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2"
                data-testid="live-host-controls">
                <button
                  onClick={togglePause}
                  disabled={hostBusy}
                  data-testid="live-pause-btn"
                  className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest backdrop-blur shadow-lg flex items-center gap-1.5 transition disabled:opacity-50 ${
                    isPaused
                      ? "bg-[#D4FF00] text-black"
                      : "bg-white/10 text-white hover:bg-[#D4FF00] hover:text-black"
                  }`}>
                  {isPaused ? <Play size={11}/> : <Pause size={11}/>}
                  {isPaused ? "Resume" : "Pause"}
                </button>
                <button
                  onClick={endStream}
                  disabled={hostBusy}
                  data-testid="live-end-btn"
                  className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest bg-[#FF007F] text-white hover:bg-[#FF007F]/80 backdrop-blur shadow-lg flex items-center gap-1.5 transition disabled:opacity-50">
                  <Square size={11} className="fill-white"/>
                  End
                </button>
              </div>
            )}

            {/* nav arrows — stacked on the right edge for a cleaner look */}
            {allStreams.length > 1 && (
              <>
                <button onClick={()=>goTo(-1)} data-testid="live-prev-btn"
                  aria-label="Previous stream"
                  className="absolute top-1/2 -translate-y-12 right-2 lg:right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur hover:bg-[#D4FF00] hover:text-black text-white flex items-center justify-center transition active:scale-90 ring-1 ring-white/10">
                  <ChevronUp size={20}/>
                </button>
                <button onClick={()=>goTo(1)} data-testid="live-next-btn"
                  aria-label="Next stream"
                  className="absolute top-1/2 translate-y-2 right-2 lg:right-4 w-10 h-10 rounded-full bg-black/50 backdrop-blur hover:bg-[#D4FF00] hover:text-black text-white flex items-center justify-center transition active:scale-90 ring-1 ring-white/10">
                  <ChevronDown size={20}/>
                </button>
                <div className="absolute top-14 left-3 bg-black/60 backdrop-blur text-white text-[10px] px-2 py-0.5 rounded-full font-mono-display z-10 ring-1 ring-white/10">
                  {Math.max(idx, 0)+1} / {allStreams.length}
                </div>
              </>
            )}

            {/* First-visit gesture hint — auto-hides after 3s, never shows again */}
            {swipeHint && (
              <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center gap-3 z-10 animate-fade-in" data-testid="live-swipe-hint">
                {allStreams.length > 1 && (
                  <div className="bg-black/75 backdrop-blur text-white px-5 py-3 rounded-2xl text-xs font-bold uppercase tracking-widest flex flex-col items-center gap-1 shadow-2xl border border-white/10">
                    <ChevronUp size={18} className="text-[#D4FF00] animate-bounce"/>
                    <span>Flick up to switch streams</span>
                    <span className="text-[10px] text-[#A1A1AA] normal-case font-normal tracking-normal">Slow scroll to read chat below</span>
                  </div>
                )}
              </div>
            )}

            {/* Bottom gradient lift for legibility */}
            <div className="absolute bottom-0 left-0 right-0 h-44 bg-gradient-to-t from-black/95 via-black/60 to-transparent pointer-events-none"/>

            {/* title bottom */}
            <div className="absolute bottom-20 lg:bottom-24 left-4 right-20">
              <div className="font-display text-white font-black text-2xl sm:text-3xl uppercase leading-[0.95] [text-shadow:0_2px_12px_rgba(0,0,0,0.7)]">{view.stream.title}</div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="text-sm font-bold text-[#D4FF00]">@{view.stream.host_name}</div>
                {view.store?.name && (
                  <Link to={`/store/${view.store?.store_id}`} className="text-[11px] text-white/70 hover:text-[#D4FF00] flex items-center gap-1">
                    · <Store size={10}/> {view.store.name}
                  </Link>
                )}
              </div>
            </div>

            {reactions.map(r => (
              <div key={r.id} className="floating-emoji" style={{right: `${r.x}px`, bottom: "120px"}}>{r.e}</div>
            ))}

            {view.pinned_products?.length > 0 && (
              <button onClick={()=>setPinnedOpen(!pinnedOpen)} data-testid="pinned-toggle"
                aria-label={`${view.pinned_products.length} pinned drops`}
                className="absolute bottom-4 left-4 bg-[#D4FF00] text-black pl-1 pr-3 py-1 rounded-full text-xs font-bold flex items-center gap-2 active:scale-95 shadow-[0_8px_24px_rgba(212,255,0,0.35)] hover:shadow-[0_8px_32px_rgba(212,255,0,0.55)] transition">
                <span className="relative flex">
                  {view.pinned_products.slice(0, 2).map((p, i) => (
                    <img key={p.product_id} src={p.images?.[0]} alt=""
                      className={`w-9 h-9 rounded-full object-cover ring-2 ring-[#D4FF00] ${i === 1 ? "-ml-3" : ""}`}/>
                  ))}
                </span>
                <span className="flex items-center gap-1">
                  <Pin size={11}/> {view.pinned_products.length} pinned drop{view.pinned_products.length > 1 ? "s" : ""}
                </span>
              </button>
            )}

            <button onClick={sendReact} data-testid="react-btn"
              aria-label="Send reaction"
              className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-[#FF007F] text-white flex items-center justify-center active:scale-90 shadow-[0_8px_24px_rgba(255,0,127,0.4)] hover:shadow-[0_8px_32px_rgba(255,0,127,0.6)] transition heart-pulse">
              <Heart size={20} className="fill-white"/>
            </button>
          </div>

          {pinnedOpen && (
            <>
              {/* backdrop tap-to-close (only over the video) */}
              <button onClick={()=>setPinnedOpen(false)} aria-label="Close pinned drops"
                className="absolute inset-0 z-10 bg-black/30 backdrop-blur-[2px]" data-testid="pinned-backdrop"/>
              <div className="absolute bottom-0 left-0 right-0 z-20 bg-[#1A0F1F]/95 backdrop-blur-xl border-t border-[#3D2845] rounded-t-3xl p-4 max-h-[60vh] overflow-y-auto animate-slide-up shadow-[0_-12px_40px_rgba(0,0,0,0.6)]">
                <div className="w-10 h-1 bg-[#3F3F46] rounded-full mx-auto mb-3"/>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-black uppercase text-base">Pinned drops</div>
                    <div className="text-[11px] text-[#A1A1AA]">Tap any item to add to cart</div>
                  </div>
                  <button onClick={()=>setPinnedOpen(false)}
                    className="w-8 h-8 rounded-full bg-[#3D2845] flex items-center justify-center hover:bg-[#3F3F46]">
                    <X size={14}/>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {view.pinned_products.map(p => (
                    <button key={p.product_id} onClick={()=>{setSelectedP(p); setPinnedOpen(false);}} data-testid={`pinned-${p.product_id}`}
                      className="text-left bg-[#261534] hover:bg-[#3D2845] rounded-2xl overflow-hidden border border-[#3D2845] hover:border-[#D4FF00]/60 transition group">
                      <div className="aspect-square overflow-hidden">
                        <img src={p.images?.[0]} alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-300"/>
                      </div>
                      <div className="p-2.5">
                        <div className="font-bold text-xs line-clamp-1">{p.name}</div>
                        <div className="text-[#D4FF00] font-display font-black text-base mt-0.5">{fmtMoney(p.price)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* CHAT */}
        <aside className="flex flex-col h-[60vh] lg:h-[85vh] bg-[#0A0A0C] border-l border-[#3D2845]">
          <div className="px-4 pt-4 pb-3 border-b border-[#3D2845] flex items-center gap-2 sticky top-0 bg-[#0A0A0C] z-10">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF007F] opacity-75 animate-ping"/>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF007F]"/>
            </span>
            <span className="font-display font-black uppercase text-sm tracking-wider">Live chat</span>
            <span className="ml-auto text-[10px] text-[#A1A1AA] flex items-center gap-1">
              <Users size={10}/> {view.stream.viewers || 0}
            </span>
          </div>
          <div ref={chatRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 scroll-smooth">
            {chat.length === 0 && (
              <div className="text-center text-[#52525B] text-xs py-8">
                Be the first to say something 👋
              </div>
            )}
            {chat.map((m, i) => {
              const isHost = m.user_id && view.stream.host_id && m.user_id === view.stream.host_id;
              const isMe = user && m.user_id === user.user_id;
              return (
                <div key={m.chat_id || i} className="flex items-start gap-2 fade-up">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ring-2 ${
                    isHost ? "bg-[#D4FF00] text-black ring-[#D4FF00]/40"
                          : isMe ? "bg-[#FF007F] text-white ring-[#FF007F]/40"
                                 : "bg-[#3D2845] text-[#FAFAFA] ring-transparent"}`}>
                    {m.user_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="text-sm flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-bold text-[12px] ${isHost ? "text-[#D4FF00]" : isMe ? "text-[#FF007F]" : "text-[#FAFAFA]"}`}>
                        {m.user_name}
                      </span>
                      {isHost && (
                        <span className="text-[8px] uppercase tracking-widest font-black bg-[#D4FF00] text-black px-1.5 py-0.5 rounded">Host</span>
                      )}
                    </div>
                    <div className="text-white/90 text-[13px] leading-snug break-words">{m.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-3 border-t border-[#3D2845] bg-[#0A0A0C]">
            <div className="relative">
              <input
                data-testid="chat-input"
                value={msg}
                onChange={e=>setMsg(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&sendMsg()}
                placeholder={user?"Say something…":"Login to chat"}
                disabled={!user}
                className="w-full bg-[#261534] border border-[#3D2845] focus:border-[#D4FF00] rounded-full pl-4 pr-12 py-2.5 text-sm text-white placeholder:text-[#52525B] outline-none transition disabled:opacity-50"
              />
              <button data-testid="chat-send" onClick={sendMsg}
                disabled={!user || !msg.trim()}
                aria-label="Send message"
                className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#D4FF00] text-black flex items-center justify-center active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed transition">
                <Send size={14}/>
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Product quick sheet */}
      {selectedP && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={()=>setSelectedP(null)}>
          <div className="bg-[#261534] w-full max-w-md rounded-3xl p-4 max-h-[80vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
            <div className="flex justify-end"><button onClick={()=>setSelectedP(null)}><X/></button></div>
            <img src={selectedP.images?.[0]} className="w-full aspect-square object-cover rounded-2xl mb-3"/>
            <div className="font-display font-black text-2xl uppercase">{selectedP.name}</div>
            <div className="text-[#D4FF00] font-display font-black text-3xl my-2">{fmtMoney(selectedP.price)}</div>
            <p className="text-sm text-[#A1A1AA] mb-4">{selectedP.description}</p>
            {selectedP.sizes?.length > 0 && (
              <div className="mb-4">
                <div className="text-xs uppercase font-bold mb-2">Size</div>
                <div className="flex flex-wrap gap-2">
                  {selectedP.sizes.map(s => (
                    <button key={s} onClick={()=>setSelectedSize(s)} data-testid={`live-size-${s}`}
                      className={`px-4 py-2 rounded-xl border-2 text-sm font-bold uppercase ${selectedSize===s?"border-[#D4FF00] bg-[#D4FF00]/10 text-[#D4FF00]":"border-[#3D2845]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="live-add-to-cart" onClick={handleAddToCart} className="btn-primary w-full">Add to cart</button>
            <Link to={`/product/${selectedP.product_id}`} className="btn-secondary w-full inline-block text-center mt-2">View full details</Link>
          </div>
        </div>
      )}
    </div>
  );
}
